import {
  NAV_APP_LABELS,
  NAV_APPS,
  REMINDER_OPTIONS,
  ROLE_LABELS,
  formatDuration,
  type CurrentUser,
  type NavApp,
  type SettingsUpdateRequest,
} from '@kominiarz/shared';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, ListChecks, LogOut } from 'lucide-react';
import { BaseLocationSection } from './settings/BaseLocationSection';
import { DeviceNotificationsSection } from '../components/push/DeviceNotificationsSection';
import { setCurrentUser, useCurrentUser, useLogout } from '../auth/useAuth';
import { Avatar } from '../components/Avatar';
import { ActionRow, List } from '../components/List';
import { ChoiceGroup } from '../components/form/ChoiceGroup';
import { SelectInput } from '../components/form/SelectInput';
import { Switch } from '../components/form/Switch';
import { PageBody } from '../components/PageBody';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { useToast } from '../components/Toast';
import { api, errorMessage } from '../lib/api';
import { ME_KEY, queryClient } from '../lib/queryClient';
import styles from './SettingsPage.module.css';

/** Morning summary times offered: 5:00–10:00 every 30 minutes. */
const MORNING_TIMES = Array.from({ length: 11 }, (_, i) => {
  const minutes = 5 * 60 + i * 30;
  const t = `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
  return { value: t, label: t };
});

const REMINDER_CHOICES = [
  ...REMINDER_OPTIONS.map((m) => ({ value: String(m), label: formatDuration(m) })),
  { value: 'off', label: 'Wyłączone' },
];

function useSaveSettings() {
  const toast = useToast();
  return useMutation({
    mutationFn: (body: SettingsUpdateRequest) =>
      api<{ user: CurrentUser }>('/me/settings', { method: 'PATCH', body }),
    // Show the new value immediately; roll back if saving fails.
    onMutate: (body) => {
      const previous = queryClient.getQueryData(ME_KEY);
      queryClient.setQueryData(ME_KEY, (old: { user: CurrentUser; csrfToken: string } | undefined) =>
        old ? { ...old, user: { ...old.user, settings: { ...old.user.settings, ...body } } } : old,
      );
      return { previous };
    },
    onError: (err, _body, context) => {
      queryClient.setQueryData(ME_KEY, context?.previous);
      toast.error(errorMessage(err));
    },
    onSuccess: (data) => {
      setCurrentUser(data.user);
      toast.success('Zapisano ustawienia');
    },
  });
}

export function SettingsPage() {
  const user = useCurrentUser();
  const save = useSaveSettings();
  const logout = useLogout();
  const s = user.settings;

  return (
    <>
      <PageHeader title="Ustawienia" />
      <PageBody>
        <div className={styles.columns}>
          <Section title="Twoje konto" flush>
            <div className={styles.profile}>
              <Avatar name={user.name} color={user.color} size="lg" />
              <span className={styles.profileText}>
                <span className={styles.profileName}>{user.name}</span>
                <span className={styles.profileMeta}>
                  {ROLE_LABELS[user.role]}, login: {user.login}
                </span>
              </span>
            </div>
            <List divided>
              <ActionRow icon={KeyRound} to="/ustawienia/haslo">
                Zmień hasło
              </ActionRow>
              <ActionRow icon={LogOut} onClick={() => logout.mutate()}>
                Wyloguj się
              </ActionRow>
            </List>
          </Section>

          <DeviceNotificationsSection />

          <Section title="Przypomnienia" description="Przypomnienia dostaje osoba, która jedzie na wizytę.">
            <Switch
              label="Poranne podsumowanie dnia"
              description="Ile masz dziś wizyt i o której pierwsza."
              checked={s.morningSummaryEnabled}
              onChange={(v) => save.mutate({ morningSummaryEnabled: v })}
            />
            <SelectInput
              label="Godzina podsumowania"
              value={s.morningSummaryTime}
              options={MORNING_TIMES}
              disabled={!s.morningSummaryEnabled}
              onChange={(e) => save.mutate({ morningSummaryTime: e.target.value })}
            />
            <ChoiceGroup
              legend="Przypomnienie przed wizytą"
              value={s.reminderMinutes === null ? 'off' : String(s.reminderMinutes)}
              options={REMINDER_CHOICES}
              columns={3}
              onChange={(v) => save.mutate({ reminderMinutes: v === 'off' ? null : Number(v) })}
            />
          </Section>

          <Section title="Nawigacja" description="Przycisk „Nawiguj” przy wizycie otworzy wybraną aplikację.">
            <ChoiceGroup<NavApp>
              legend="Otwieraj trasę w"
              value={s.navApp}
              options={NAV_APPS.map((a) => ({ value: a, label: NAV_APP_LABELS[a] }))}
              onChange={(v) => save.mutate({ navApp: v })}
            />
          </Section>

          {user.role === 'admin' && <BaseLocationSection />}

          {user.role === 'admin' && (
            <Section title="Słowniki" description="Listy wyboru używane w formularzach." flush>
              <List>
                <ActionRow icon={ListChecks} to="/ustawienia/uslugi">
                  Typy usług
                </ActionRow>
              </List>
            </Section>
          )}

          {user.role === 'admin' && (
            <Section title="Powiadomienia dla kierownika">
              <Switch
                label="Gdy pracownik doda wizytę lub oznaczy ją jako wykonaną"
                checked={s.notifyWorkerActivity}
                onChange={(v) => save.mutate({ notifyWorkerActivity: v })}
              />
            </Section>
          )}
        </div>
      </PageBody>
    </>
  );
}
